import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Truck, MapPin, Check } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';
import Button from './components/Button';

const Checkout = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '', email: '', phone: '', address: '', city: '', state: '', pincode: '',
        cardNumber: '', cardName: '', expiryDate: '', cvv: '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        } else {
            navigate('/template1/orders');
        }
    };

    const steps = [
        { number: 1, title: 'Shipping', icon: Truck },
        { number: 2, title: 'Payment', icon: CreditCard },
        { number: 3, title: 'Review', icon: Check },
    ];

    const orderItems = [
        { name: 'Chanderi Silk Saree', price: 8999, quantity: 1, size: 'Free Size' },
        { name: 'Cotton Chikankari Kurta', price: 2999, quantity: 2, size: 'L' },
    ];

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 0;
    const total = subtotal + shipping;

    const inputClass = "w-full px-4 py-2.5 md:py-3 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#2D6A4F] transition-colors bg-white";

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header />

            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10 lg:py-12">
                <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6 md:mb-8">Checkout</h1>

                {/* Progress Steps */}
                <div className="flex justify-between items-center mb-8 md:mb-12 max-w-lg mx-auto">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        const isActive = currentStep >= step.number;
                        const isCompleted = currentStep > step.number;
                        return (
                            <React.Fragment key={step.number}>
                                <div className="flex flex-col items-center">
                                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center border-2 transition-colors ${
                                        isActive ? 'bg-[#2D6A4F] border-[#2D6A4F] text-white' : 'border-[#E5E7EB] text-[#9CA3AF]'
                                    }`}>
                                        {isCompleted ? <Check size={18} /> : <Icon size={18} />}
                                    </div>
                                    <span className={`mt-1.5 text-xs font-medium ${isActive ? 'text-[#2D6A4F]' : 'text-[#9CA3AF]'}`}>{step.title}</span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-3 ${currentStep > step.number ? 'bg-[#2D6A4F]' : 'bg-[#E5E7EB]'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                    {/* Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                            {currentStep === 1 && (
                                <div className="border border-[#E5E7EB] p-5 md:p-6">
                                    <div className="flex items-center gap-3 mb-5">
                                        <MapPin className="text-[#2D6A4F]" size={20} />
                                        <h2 className="text-lg md:text-xl font-semibold text-[#1A1A1A]">Shipping Information</h2>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Full Name *</label>
                                            <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} required className={inputClass} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Email *</label>
                                                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Phone *</label>
                                                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className={inputClass} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Address *</label>
                                            <input type="text" name="address" value={formData.address} onChange={handleInputChange} required className={inputClass} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">City *</label>
                                                <input type="text" name="city" value={formData.city} onChange={handleInputChange} required className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">State *</label>
                                                <input type="text" name="state" value={formData.state} onChange={handleInputChange} required className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">PIN Code *</label>
                                                <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} required className={inputClass} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="border border-[#E5E7EB] p-5 md:p-6">
                                    <div className="flex items-center gap-3 mb-5">
                                        <CreditCard className="text-[#2D6A4F]" size={20} />
                                        <h2 className="text-lg md:text-xl font-semibold text-[#1A1A1A]">Payment Information</h2>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Card Number *</label>
                                            <input type="text" name="cardNumber" value={formData.cardNumber} onChange={handleInputChange} placeholder="1234 5678 9012 3456" required className={inputClass} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Cardholder Name *</label>
                                            <input type="text" name="cardName" value={formData.cardName} onChange={handleInputChange} required className={inputClass} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">Expiry *</label>
                                                <input type="text" name="expiryDate" value={formData.expiryDate} onChange={handleInputChange} placeholder="MM/YY" required className={inputClass} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-[#1A1A1A] mb-1.5">CVV *</label>
                                                <input type="text" name="cvv" value={formData.cvv} onChange={handleInputChange} placeholder="123" required className={inputClass} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 3 && (
                                <div className="border border-[#E5E7EB] p-5 md:p-6">
                                    <h2 className="text-lg md:text-xl font-semibold text-[#1A1A1A] mb-5">Review Your Order</h2>
                                    <div className="space-y-5">
                                        <div>
                                            <h3 className="text-sm font-medium text-[#1A1A1A] mb-1.5">Shipping Address</h3>
                                            <p className="text-sm text-[#6B7280]">
                                                {formData.fullName}<br />{formData.address}<br />{formData.city}, {formData.state} {formData.pincode}<br />{formData.phone}
                                            </p>
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-medium text-[#1A1A1A] mb-1.5">Order Items</h3>
                                            <div className="space-y-2">
                                                {orderItems.map((item, i) => (
                                                    <div key={i} className="flex justify-between text-sm text-[#6B7280]">
                                                        <span>{item.name} (x{item.quantity})</span>
                                                        <span>{'\u20B9'}{(item.price * item.quantity).toLocaleString('en-IN')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3">
                                {currentStep > 1 && (
                                    <Button type="button" variant="secondary" size="lg" onClick={() => setCurrentStep(currentStep - 1)}>
                                        Back
                                    </Button>
                                )}
                                <Button type="submit" variant="primary" size="lg" fullWidth>
                                    {currentStep === 3 ? 'Place Order' : 'Continue'}
                                </Button>
                            </div>
                        </form>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="border border-[#E5E7EB] p-5 md:p-6 sticky top-24">
                            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-5">Order Summary</h2>
                            <div className="space-y-3 mb-5">
                                {orderItems.map((item, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                        <span className="text-[#6B7280]">{item.name} (x{item.quantity})</span>
                                        <span className="font-medium text-[#1A1A1A]">{'\u20B9'}{(item.price * item.quantity).toLocaleString('en-IN')}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-[#E5E7EB] pt-4 space-y-2.5">
                                <div className="flex justify-between text-sm text-[#6B7280]">
                                    <span>Subtotal</span><span>{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-sm text-[#6B7280]">
                                    <span>Shipping</span><span>{shipping === 0 ? 'Free' : `\u20B9${shipping}`}</span>
                                </div>
                                <div className="flex justify-between text-lg font-semibold text-[#1A1A1A] pt-3 border-t border-[#E5E7EB]">
                                    <span>Total</span><span>{'\u20B9'}{total.toLocaleString('en-IN')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Checkout;
